import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react'
import { CheckIcon, CopyIcon, ExternalLinkIcon, InfoOutlineIcon, WarningTwoIcon } from '@chakra-ui/icons'
import { FiRotateCcw } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import {
  ContractDeploymentRecord,
  EvmOperationEnvelope,
  EvmPendingOperationItem,
  RefundHistoryEntry,
  RefundUserPayment,
  SolanaOperationEnvelope,
  SolanaPendingMetaRefundItem,
} from '@/types'
import { useOrderRefundData, useOrderRefundHistory, useMetaRefundMutation } from '@/hooks/useAdminData'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useAuth } from '@/contexts/AuthContext'
import { evmOperationService } from '@/services/admin/evmOperationService'
import { refundService } from '@/services/admin/refundService'
import { solanaOperationService } from '@/services/admin/solanaOperationService'
import { formatTokenAmount as formatAmountWithDecimals } from '@/utils/amountUtils'
import { formatDateTime, formatRelativeTime, formatRefundAmount, getExplorerAddressUrl, getExplorerTxUrl, sanitizeTokenSymbol } from '@/utils/formatUtils'
import { useToast } from '@/hooks/useToast'
import { TEXT_CONSTRAINTS } from '@shared/inputConstraints'
import { validateTextWithConstraint } from '@shared/textConstraintHelpers'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import {
  getDeploymentDisplayAddresses,
  type DeploymentDisplayAddressKind,
} from '@/utils/adminAddressPresentation'

interface MetaRefundModalProps {
  isOpen: boolean
  onClose: () => void
  deployment: ContractDeploymentRecord | null
}

interface RecipientStateItem {
  address: string
  transactionHash?: string
  selected: boolean
  inputAmount: string
  maxRawAmount: string
  createdAt?: string
}

const REFUND_REASON_CONSTRAINT = TEXT_CONSTRAINTS['refund.reason']
const REASON_MIN_LENGTH = REFUND_REASON_CONSTRAINT.minLength ?? 0
const REASON_MAX_LENGTH = REFUND_REASON_CONSTRAINT.maxLength ?? 500
const REFUND_NOT_SUPPORTED_CODE = 'REFUND_NOT_SUPPORTED'
const EVM_TERMINAL_STATES = new Set(['completed', 'rejected', 'failed'])
const SOLANA_TERMINAL_STATES = new Set(['completed', 'rejected', 'failed'])
type RefundHistorySource = 'merchant' | 'platform'

interface RefundHistoryRow {
  entry: RefundHistoryEntry
  source: RefundHistorySource
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object'

const getRefundQueryErrorCode = (error: unknown): string | undefined => {
  if (!isObjectRecord(error)) {
    return undefined
  }
  const code = error.code
  return typeof code === 'string' && code.trim().length > 0 ? code.trim() : undefined
}

const getRefundQueryErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message.trim()
  }
  if (!isObjectRecord(error)) {
    return undefined
  }
  const message = error.message
  return typeof message === 'string' && message.trim().length > 0 ? message.trim() : undefined
}

const formatOperationError = (error: unknown, fallback: string): string => {
  const direct = getRefundQueryErrorMessage(error)
  if (direct) {
    return direct
  }
  if (isObjectRecord(error) && isObjectRecord(error.response)) {
    const responseData = error.response.data
    if (isObjectRecord(responseData)) {
      const responseError = responseData.error
      if (typeof responseError === 'string' && responseError.trim()) {
        return responseError.trim()
      }
      if (isObjectRecord(responseError) && typeof responseError.message === 'string') {
        return responseError.message
      }
    }
  }
  return fallback
}

const formatEvmOperationError = (error: unknown): string =>
  formatOperationError(error, 'Unable to recover the EVM refund operation')

const formatSolanaOperationError = (error: unknown): string =>
  formatOperationError(error, 'Unable to recover the Solana refund operation')

const getRefundHistorySource = (entry: RefundHistoryEntry): RefundHistorySource | null => {
  const snakeCaseSource = entry.refund_by === 'merchant' || entry.refund_by === 'platform'
    ? entry.refund_by
    : null
  const camelCaseSource = entry.refundBy === 'merchant' || entry.refundBy === 'platform'
    ? entry.refundBy
    : null

  if (snakeCaseSource && camelCaseSource && snakeCaseSource !== camelCaseSource) {
    return null
  }

  return snakeCaseSource ?? camelCaseSource
}

const getRefundHistorySortTime = (entry: RefundHistoryEntry): number => {
  if (!entry.createdAt) {
    return Number.POSITIVE_INFINITY
  }

  const parsed = Date.parse(entry.createdAt)
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
}

export function MetaRefundModal({ isOpen, onClose, deployment }: MetaRefundModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const {
    success: showToastSuccess,
    error: showToastError,
    warning: showToastWarning,
  } = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const [reason, setReason] = useState('')
  const [recipients, setRecipients] = useState<Record<string, RecipientStateItem>>({})
  const [pendingEvmRefund, setPendingEvmRefund] =
    useState<EvmPendingOperationItem | null>(null)
  const [evmRefundOperation, setEvmRefundOperation] =
    useState<EvmOperationEnvelope | null>(null)
  const [evmRefundError, setEvmRefundError] = useState<string | null>(null)
  const [isEvmRefundPreReservationFailure, setIsEvmRefundPreReservationFailure] =
    useState(false)
  const [hasQuarantinedEvmRefund, setHasQuarantinedEvmRefund] = useState(false)
  const [isRefreshingEvmRefund, setIsRefreshingEvmRefund] = useState(false)
  const [isSubmittingEvmRefund, setIsSubmittingEvmRefund] = useState(false)
  const [pendingSolanaRefund, setPendingSolanaRefund] =
    useState<SolanaPendingMetaRefundItem | null>(null)
  const [solanaRefundOperation, setSolanaRefundOperation] =
    useState<SolanaOperationEnvelope | null>(null)
  const [solanaRefundError, setSolanaRefundError] = useState<string | null>(null)
  const [hasQuarantinedSolanaRefund, setHasQuarantinedSolanaRefund] = useState(false)
  const [isRefreshingSolanaRefund, setIsRefreshingSolanaRefund] = useState(false)
  const [isSubmittingSolanaRefund, setIsSubmittingSolanaRefund] = useState(false)
  const [isSolanaScopeInspected, setIsSolanaScopeInspected] = useState(false)
  const hasShownExceedsToast = useRef(false)
  const autoRefreshKeyRef = useRef<string | null>(null)
  const solanaExecutionEpochRef = useRef(0)

  const orderBusinessId = deployment?.orderId
  const { data: refundData, isLoading: isLoadingRefundData, isFetching: isFetchingRefundData, error: refundDataError, refetch: refetchRefundData } =
    useOrderRefundData(isOpen ? orderBusinessId : undefined)
  const { data: refundHistory, isLoading: isLoadingHistory, isFetching: isFetchingHistory, error: refundHistoryError, refetch: refetchRefundHistory } =
    useOrderRefundHistory(isOpen ? orderBusinessId : undefined)
  const metaRefundMutation = useMetaRefundMutation()
  const shouldLoadRefundData = isOpen && Boolean(orderBusinessId)
  const hasRefundDataError = Boolean(refundDataError)
  const hasRefundHistoryError = Boolean(refundHistoryError)
  const hasRefundData = Boolean(refundData)
  const isRefreshingRefundWorkflow = isFetchingRefundData || isFetchingHistory
  const refundDataErrorCode = getRefundQueryErrorCode(refundDataError)
  const refundDataErrorMessage = getRefundQueryErrorMessage(refundDataError)
  const isRefundUnsupported = refundDataErrorCode === REFUND_NOT_SUPPORTED_CODE
  const refundUnsupportedMessage = isRefundUnsupported
    ? t(
        'contractRegistry.metaRefund.errors.unsupportedChain',
        'This order chain does not support Meta Refund.'
      )
    : refundDataErrorMessage ||
      t(
        'contractRegistry.metaRefund.errors.refundUnavailable',
        'Meta Refund is not available for this order. Only refund history is available.'
      )
  const canRenderRefundData = shouldLoadRefundData && !isLoadingRefundData && !hasRefundDataError && hasRefundData
  const refundDataLoadFailed =
    shouldLoadRefundData &&
    !isLoadingRefundData &&
    !isRefundUnsupported &&
    (!hasRefundData || hasRefundDataError)
  const canRenderRefundHistory = shouldLoadRefundData && !isLoadingHistory && !hasRefundHistoryError
  const refundHistoryLoadFailed = shouldLoadRefundData && !isLoadingHistory && hasRefundHistoryError
  const isRefundWorkflowLoading = shouldLoadRefundData && (isLoadingRefundData || (canRenderRefundData && isLoadingHistory))
  const canUseRefundWorkflow = canRenderRefundData && !isLoadingHistory && !hasRefundHistoryError
  const isEvmRefund = refundData?.compatibility === 'evm'
  const isSolanaRefund = refundData?.compatibility === 'solana'
  const canonicalRefundResourceId = refundService.resolveCanonicalEvmRefundResourceId(refundData)
  const canonicalSolanaRefundResourceId =
    refundService.resolveCanonicalSolanaRefundResourceId(refundData)

  const chainId = refundData?.chainId ? String(refundData.chainId) : (deployment?.chain_id || '')
  const deploymentAddressRows = useMemo(
    () => deployment ? getDeploymentDisplayAddresses(deployment) : [],
    [deployment]
  )
  const deploymentAddressLabels: Record<DeploymentDisplayAddressKind, string> = {
    payment: t('contractRegistry.deployment.details.paymentAddress', 'Payment Address'),
    contract: t('contractRegistry.table.contractAddress', 'Contract Address'),
    predicted_contract: t('contractRegistry.deployment.details.predictedAddress'),
    deployed_contract: t('contractRegistry.deployment.details.deployedAddress'),
    vault_token_account: t(
      'contractRegistry.deployment.details.vaultTokenAccount',
      'Vault Token Account'
    ),
  }
  const tokenAddress = refundData?.tokenAddress || deployment?.token_address || ''
  const tokenSymbol = useMemo(() => {
    const normalizeSnapshot = (value?: string) => {
      const normalized = sanitizeTokenSymbol(value)
      return normalized === 'USDC' || normalized === 'USDT' ? normalized : undefined
    }
    return normalizeSnapshot(refundData?.tokenSymbol)
      ?? normalizeSnapshot(deployment?.token_symbol)
  }, [deployment?.token_symbol, refundData?.tokenSymbol])
  const tokenDecimals = useMemo(() => {
    const normalizeSnapshot = (value?: number | null) => (
      typeof value === 'number'
      && Number.isSafeInteger(value)
      && value >= 0
      && value <= 255
        ? value
        : null
    )
    return normalizeSnapshot(refundData?.tokenDecimals)
      ?? normalizeSnapshot(deployment?.token_decimals)
  }, [deployment?.token_decimals, refundData?.tokenDecimals])
  const hasKnownTokenDecimals = tokenDecimals !== null
  const showTokenMetadataMissing = canUseRefundWorkflow && !hasKnownTokenDecimals
  const resolvedRefundDisabledMessage = useMemo(() => {
    if (!canUseRefundWorkflow || refundData?.canMetaRefund) {
      return undefined
    }
    switch (refundData?.metaRefundDisabledReason) {
      case 'ORDER_SETTLED':
        return t(
          'contractRegistry.metaRefund.errors.orderSettled',
          'This order has already been settled. Only refund history is available.'
        )
      case 'REFUND_WINDOW_EXPIRED':
        return t(
          'contractRegistry.metaRefund.errors.refundWindowExpired',
          'The refund window for this order has closed. Only refund history is available.'
        )
      case 'TOKEN_METADATA_MISSING':
        return t(
          'contractRegistry.metaRefund.errors.tokenMetadataMissing',
          'Token decimals are unavailable for this order. Meta Refund is disabled until refund metadata is complete.'
        )
      case 'CONTRACT_NOT_INITIALIZED':
        return t(
          'contractRegistry.metaRefund.errors.contractNotInitialized',
          'The refund contract is not initialized yet. Meta Refund is disabled until initialization is complete.'
        )
      case 'NO_CONFIRMED_PAYMENTS':
        return t(
          'contractRegistry.metaRefund.payers.empty',
          'No confirmed payments found. Meta Refund cannot be executed.'
        )
      case 'NO_REFUNDABLE_BALANCE':
        return t(
          'contractRegistry.metaRefund.errors.noAvailable',
          'No refundable balance remains for this order.'
        )
      default:
        if (refundData?.metaRefundDisabledMessage) {
          return refundData.metaRefundDisabledMessage
        }
        return t(
          'contractRegistry.metaRefund.errors.refundUnavailable',
          'Meta Refund is not available for this order. Only refund history is available.'
        )
    }
  }, [
    canUseRefundWorkflow,
    refundData?.canMetaRefund,
    refundData?.metaRefundDisabledMessage,
    refundData?.metaRefundDisabledReason,
    t,
  ])
  const canExecuteRefundWorkflow = canUseRefundWorkflow && refundData?.canMetaRefund === true

  const decimalToRaw = useCallback(
    (value: string): string => {
      if (tokenDecimals === null) {
        return '0'
      }
      if (!value) {
        return '0'
      }
      const sanitized = value.replace(/,/g, '').trim()
      if (!sanitized) {
        return '0'
      }
      const match = sanitized.match(/^\d*(?:\.\d*)?$/)
      if (!match) {
        return '0'
      }
      const [integerPartRaw, fractionalRaw = ''] = sanitized.split('.')
      const integerPart = integerPartRaw.replace(/^0+(?=\d)/, '') || '0'
      const fractionNormalized = fractionalRaw.padEnd(tokenDecimals, '0').slice(0, tokenDecimals)
      const combined = `${integerPart}${fractionNormalized}`.replace(/^0+(?=\d)/, '') || '0'
      try {
        return BigInt(combined || '0').toString()
      } catch {
        return '0'
      }
    },
    [tokenDecimals]
  )

  const modalHeaderBg = useColorModeValue('gray.50', 'gray.800')
  const summaryBg = useColorModeValue('blue.50', 'blue.900')
  const summaryBorder = useColorModeValue('blue.100', 'blue.700')
  const tableHeaderBg = useColorModeValue('gray.100', 'gray.700')
  const mutedText = useColorModeValue('gray.600', 'gray.300')

  const userPayments = useMemo(() => {
    return refundData?.userPayments ?? []
  }, [refundData?.userPayments])

  const getPaymentAmountUsd = useCallback((payment: RefundUserPayment): string => {
    const legacy = payment as RefundUserPayment & { amount_usd?: string; amount?: string }
    return legacy.amountUsd ?? legacy.amount_usd ?? legacy.amount ?? '0'
  }, [])

  const getPaymentMaxRefundableRaw = useCallback((payment: RefundUserPayment): string | null => {
    const legacy = payment as RefundUserPayment & { max_refundable?: string }
    const value = payment.maxRefundable ?? legacy.max_refundable
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
  }, [])

  const normalizeRefundAddressKey = useCallback(
    (address: string): string =>
      refundData?.compatibility === 'solana' ? address.trim() : address.trim().toLowerCase(),
    [refundData?.compatibility]
  )

  const paymentAggregates = useMemo(() => {
    const map = new Map<string, bigint>()
    userPayments.forEach((payment) => {
      const address = normalizeRefundAddressKey(payment.address || '')
      const amountRaw = BigInt(decimalToRaw(getPaymentAmountUsd(payment)))
      map.set(address, (map.get(address) || 0n) + amountRaw)
    })
    return map
  }, [userPayments, decimalToRaw, getPaymentAmountUsd, normalizeRefundAddressKey])

  const historicalRefundAggregates = useMemo(() => {
    const map = new Map<string, bigint>()
    refundHistory?.refundHistory?.forEach((entry) => {
      const metaEntries = Array.isArray(entry.refund_meta)
        ? entry.refund_meta
        : Array.isArray(entry.refundMeta)
          ? entry.refundMeta
          : []
      metaEntries.forEach((meta) => {
        if (!meta?.recipient) {
          return
        }
        const address = normalizeRefundAddressKey(meta.recipient)
        let amountRaw = 0n
        const decimalAmount = meta.amountUsd ?? meta.amount_usd
        if (decimalAmount) {
          amountRaw = BigInt(decimalToRaw(decimalAmount))
        } else if (meta.amount) {
          try {
            amountRaw = BigInt(meta.amount)
          } catch {
            amountRaw = 0n
          }
        }
        map.set(address, (map.get(address) || 0n) + amountRaw)
      })
    })
    return map
  }, [refundHistory?.refundHistory, decimalToRaw, normalizeRefundAddressKey])

  const onchainAvailableByAddress = useMemo(() => {
    const map = new Map<string, bigint>()
    let hasAvailability = false

    userPayments.forEach((payment) => {
      const address = normalizeRefundAddressKey(payment.address || '')
      const raw = getPaymentMaxRefundableRaw(payment)
      if (!address || raw === null) {
        return
      }

      try {
        map.set(address, BigInt(raw))
        hasAvailability = true
      } catch {
        map.set(address, 0n)
        hasAvailability = true
      }
    })

    return hasAvailability ? map : null
  }, [userPayments, getPaymentMaxRefundableRaw, normalizeRefundAddressKey])

  const availableByAddress = useMemo(() => {
    if (onchainAvailableByAddress) {
      const map = new Map<string, bigint>()
      paymentAggregates.forEach((_paidRaw, address) => {
        map.set(address, onchainAvailableByAddress.get(address) ?? 0n)
      })
      return map
    }

    if (refundData?.canMetaRefund === true) {
      const map = new Map<string, bigint>()
      paymentAggregates.forEach((_paidRaw, address) => {
        map.set(address, 0n)
      })
      return map
    }

    const map = new Map<string, bigint>()
    paymentAggregates.forEach((paidRaw, address) => {
      const refundedRaw = historicalRefundAggregates.get(address) || 0n
      const available = paidRaw > refundedRaw ? paidRaw - refundedRaw : 0n
      map.set(address, available)
    })
    return map
  }, [
    paymentAggregates,
    historicalRefundAggregates,
    onchainAvailableByAddress,
    refundData?.canMetaRefund,
  ])

  const availablePerPayment = useMemo(() => {
    const remaining = new Map(availableByAddress)
    return userPayments.map((payment) => {
      const address = normalizeRefundAddressKey(payment.address || '')
      const paymentRaw = BigInt(decimalToRaw(getPaymentAmountUsd(payment)))
      const currentRemaining = remaining.get(address)
      if (currentRemaining === undefined) {
        remaining.set(address, 0n)
        return paymentRaw
      }
      const available = currentRemaining > paymentRaw ? paymentRaw : currentRemaining
      remaining.set(address, currentRemaining > paymentRaw ? currentRemaining - paymentRaw : 0n)
      return available
    })
  }, [userPayments, availableByAddress, decimalToRaw, getPaymentAmountUsd, normalizeRefundAddressKey])

  const totalAvailableRaw = useMemo(() => {
    // Sum per-recipient availability instead of relying on aggregate totals, which can
    // temporarily drift when history rows include ancillary recipients (e.g. platform fees)
    let total = 0n
    availableByAddress.forEach((value) => {
      total += value
    })
    return total
  }, [availableByAddress])

  const formattedTotalAvailable = useMemo(() => {
    return formatRefundAmount(
      totalAvailableRaw.toString(),
      chainId || '0',
      tokenAddress,
      tokenSymbol,
      tokenDecimals ?? undefined
    )
  }, [totalAvailableRaw, chainId, tokenAddress, tokenSymbol, tokenDecimals])

  const hasAvailableRefund = totalAvailableRaw > 0n
  const showNoConfirmedPayments =
    canExecuteRefundWorkflow && (refundData?.userPayments?.length ?? 0) === 0

  const getPaymentKey = useCallback((payment: RefundUserPayment, index: number): string => {
    const normalizedAddress = (payment.address || `unknown-${index}`).toLowerCase()
    const txComponent = (payment.transactionHash || `tx-${index}`).toLowerCase()
    return `${normalizedAddress}-${txComponent}-${index}`
  }, [])

  const handleSelectAll = useCallback(() => {
    if (!canExecuteRefundWorkflow || !refundData?.userPayments) {
      return
    }
    const updated: Record<string, RecipientStateItem> = {}
    refundData.userPayments.forEach((payment, index) => {
      const key = getPaymentKey(payment, index)
      const availableRaw = availablePerPayment[index] ?? BigInt(decimalToRaw(getPaymentAmountUsd(payment)))
      const formattedAmount = hasKnownTokenDecimals
        ? formatAmountWithDecimals(availableRaw.toString(), tokenDecimals as number, 2)
        : ''
      updated[key] = {
        address: payment.address,
        transactionHash: payment.transactionHash,
        selected: availableRaw > 0n,
        inputAmount: formattedAmount,
        maxRawAmount: availableRaw.toString(),
        createdAt: payment.createdAt,
      }
    })
    setRecipients(updated)
  }, [
    refundData?.userPayments,
    canExecuteRefundWorkflow,
    getPaymentKey,
    availablePerPayment,
    decimalToRaw,
    getPaymentAmountUsd,
    hasKnownTokenDecimals,
    tokenDecimals,
  ])

  const handleClearSelection = useCallback(() => {
    setRecipients((prev) => {
      const cleared: Record<string, RecipientStateItem> = {}
      Object.entries(prev).forEach(([key, item]) => {
        cleared[key] = {
          ...item,
          selected: false,
        }
      })
      return cleared
    })
  }, [])

  const syncPendingEvmRefund = useCallback((idempotencyKey: string) => {
    if (!user?.id || !canonicalRefundResourceId) {
      return
    }
    const hydrated = evmOperationService.hydratePending(user.id, currentEnv)
    const resourceItems = hydrated.items.filter(item =>
      item.operationType === 'evm_platform_meta_refund'
      && item.resourceType === 'refund_order'
      && item.resourceId === canonicalRefundResourceId
    )
    setPendingEvmRefund(
      resourceItems.find(item => item.idempotencyKey === idempotencyKey)
      ?? resourceItems.at(-1)
      ?? null
    )
    if (hydrated.quarantinedKeys.length > 0) {
      setHasQuarantinedEvmRefund(true)
    }
  }, [canonicalRefundResourceId, currentEnv, user?.id])

  const reconcileEvmRefund = useCallback(async (item: EvmPendingOperationItem) => {
    if (!user?.id) {
      throw new Error('Admin identity is unavailable')
    }
    setIsRefreshingEvmRefund(true)
    try {
      const operation = await evmOperationService.reconcilePending(
        user.id,
        currentEnv,
        item
      )
      setEvmRefundOperation(operation)
      setEvmRefundError(null)
      setIsEvmRefundPreReservationFailure(false)
      syncPendingEvmRefund(item.idempotencyKey)
      if (EVM_TERMINAL_STATES.has(operation.state)) {
        void Promise.all([refetchRefundData(), refetchRefundHistory()])
      }
      return operation
    } catch (error) {
      setEvmRefundError(formatEvmOperationError(error))
      setIsEvmRefundPreReservationFailure(
        evmOperationService.isPreReservationError(error)
      )
      throw error
    } finally {
      setIsRefreshingEvmRefund(false)
    }
  }, [
    currentEnv,
    refetchRefundData,
    refetchRefundHistory,
    syncPendingEvmRefund,
    user?.id,
  ])

  const loadLatestEvmRefund = useCallback(async () => {
    if (!canonicalRefundResourceId) {
      throw new Error('Canonical refund resource identity is unavailable')
    }
    setIsRefreshingEvmRefund(true)
    try {
      const result = await evmOperationService.list(
        'refund_order',
        canonicalRefundResourceId,
        currentEnv,
        0,
        100
      )
      const operation = result.items.at(-1) ?? null
      setEvmRefundOperation(operation)
      setEvmRefundError(null)
      setIsEvmRefundPreReservationFailure(false)
      return operation
    } catch (error) {
      setEvmRefundError(formatEvmOperationError(error))
      setIsEvmRefundPreReservationFailure(false)
      throw error
    } finally {
      setIsRefreshingEvmRefund(false)
    }
  }, [canonicalRefundResourceId, currentEnv])

  const refreshEvmRefund = useCallback(async () => {
    if (pendingEvmRefund) {
      return reconcileEvmRefund(pendingEvmRefund)
    }
    return loadLatestEvmRefund()
  }, [loadLatestEvmRefund, pendingEvmRefund, reconcileEvmRefund])

  useEffect(() => {
    if (
      !isOpen
      || !isEvmRefund
      || !canonicalRefundResourceId
      || pendingEvmRefund
      || !evmRefundOperation
      || !EVM_TERMINAL_STATES.has(evmRefundOperation.state)
    ) {
      return
    }
    void loadLatestEvmRefund().catch(() => undefined)
  }, [
    canonicalRefundResourceId,
    evmRefundOperation,
    isEvmRefund,
    isOpen,
    loadLatestEvmRefund,
    pendingEvmRefund,
  ])

  const syncPendingSolanaRefund = useCallback((idempotencyKey?: string) => {
    if (!user?.id || !canonicalSolanaRefundResourceId) {
      return
    }
    const hydrated = solanaOperationService.hydratePending(
      user.id,
      currentEnv,
      canonicalSolanaRefundResourceId
    )
    const hasInvalidScope = hydrated.quarantinedKeys.length > 0 || hydrated.items.length > 1
    setHasQuarantinedSolanaRefund(hasInvalidScope)
    setPendingSolanaRefund(
      hydrated.items.find(item => item.idempotencyKey === idempotencyKey)
      ?? hydrated.items.at(-1)
      ?? null
    )
    if (hydrated.items.length > 1) {
      setSolanaRefundError('Multiple Solana refund operations are pending for this order.')
    }
  }, [canonicalSolanaRefundResourceId, currentEnv, user?.id])

  const loadNonterminalSolanaRefund = useCallback(async () => {
    if (!canonicalSolanaRefundResourceId) {
      throw new Error('Canonical Solana refund resource identity is unavailable')
    }
    const executionEpoch = solanaExecutionEpochRef.current
    setIsRefreshingSolanaRefund(true)
    setIsSolanaScopeInspected(false)
    try {
      const operations = await solanaOperationService.listNonterminal(
        canonicalSolanaRefundResourceId,
        currentEnv
      )
      const operation = operations.at(-1) ?? null
      await Promise.all([refetchRefundData(), refetchRefundHistory()])
      if (executionEpoch !== solanaExecutionEpochRef.current) {
        return operation
      }
      setSolanaRefundOperation(operation)
      setSolanaRefundError(null)
      setIsSolanaScopeInspected(true)
      return operation
    } catch (error) {
      if (executionEpoch === solanaExecutionEpochRef.current) {
        setSolanaRefundError(formatSolanaOperationError(error))
      }
      throw error
    } finally {
      if (executionEpoch === solanaExecutionEpochRef.current) {
        setIsRefreshingSolanaRefund(false)
      }
    }
  }, [
    canonicalSolanaRefundResourceId,
    currentEnv,
    refetchRefundData,
    refetchRefundHistory,
  ])

  const reconcileSolanaRefund = useCallback(async (
    item: SolanaPendingMetaRefundItem,
    resumeUnsent: boolean
  ) => {
    if (!user?.id) {
      throw new Error('Admin identity is unavailable')
    }
    const executionEpoch = solanaExecutionEpochRef.current
    setIsRefreshingSolanaRefund(true)
    try {
      if (!item.lastAttemptAt && !resumeUnsent) {
        setSolanaRefundError(
          'This Solana refund intent was persisted before submission. Resume it explicitly.'
        )
        setIsSolanaScopeInspected(true)
        return null
      }
      const operation = item.lastAttemptAt
        ? await solanaOperationService.reconcilePending(user.id, currentEnv, item)
        : await refundService.submitSolanaMetaRefundOperation(user.id, item, currentEnv)
      if (executionEpoch !== solanaExecutionEpochRef.current) {
        return operation
      }
      setSolanaRefundOperation(operation)
      setSolanaRefundError(null)
      setIsSolanaScopeInspected(true)
      syncPendingSolanaRefund(item.idempotencyKey)
      if (SOLANA_TERMINAL_STATES.has(operation.state)) {
        await Promise.all([refetchRefundData(), refetchRefundHistory()])
      }
      return operation
    } catch (error) {
      if (executionEpoch === solanaExecutionEpochRef.current) {
        setSolanaRefundError(formatSolanaOperationError(error))
        setIsSolanaScopeInspected(false)
      }
      throw error
    } finally {
      if (executionEpoch === solanaExecutionEpochRef.current) {
        setIsRefreshingSolanaRefund(false)
      }
    }
  }, [
    currentEnv,
    refetchRefundData,
    refetchRefundHistory,
    syncPendingSolanaRefund,
    user?.id,
  ])

  const refreshSolanaRefund = useCallback(async () => {
    if (pendingSolanaRefund) {
      return reconcileSolanaRefund(pendingSolanaRefund, true)
    }
    return loadNonterminalSolanaRefund()
  }, [loadNonterminalSolanaRefund, pendingSolanaRefund, reconcileSolanaRefund])

  useEffect(() => {
    if (!isOpen) {
      solanaExecutionEpochRef.current += 1
      setRecipients({})
      setReason('')
      hasShownExceedsToast.current = false
      autoRefreshKeyRef.current = null
      setPendingEvmRefund(null)
      setEvmRefundOperation(null)
      setEvmRefundError(null)
      setIsEvmRefundPreReservationFailure(false)
      setHasQuarantinedEvmRefund(false)
      setIsRefreshingEvmRefund(false)
      setIsSubmittingEvmRefund(false)
      setPendingSolanaRefund(null)
      setSolanaRefundOperation(null)
      setSolanaRefundError(null)
      setHasQuarantinedSolanaRefund(false)
      setIsRefreshingSolanaRefund(false)
      setIsSubmittingSolanaRefund(false)
      setIsSolanaScopeInspected(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !orderBusinessId) {
      autoRefreshKeyRef.current = null
      return
    }

    const refreshKey = `${currentEnv}:${orderBusinessId}`
    if (autoRefreshKeyRef.current === refreshKey) {
      return
    }

    autoRefreshKeyRef.current = refreshKey
    void Promise.all([refetchRefundData(), refetchRefundHistory()])
  }, [currentEnv, isOpen, orderBusinessId, refetchRefundData, refetchRefundHistory])

  useEffect(() => {
    solanaExecutionEpochRef.current += 1
    setRecipients({})
    setReason('')
    hasShownExceedsToast.current = false
    setPendingEvmRefund(null)
    setEvmRefundOperation(null)
    setEvmRefundError(null)
    setIsEvmRefundPreReservationFailure(false)
    setHasQuarantinedEvmRefund(false)
    setPendingSolanaRefund(null)
    setSolanaRefundOperation(null)
    setSolanaRefundError(null)
    setHasQuarantinedSolanaRefund(false)
    setIsRefreshingSolanaRefund(false)
    setIsSubmittingSolanaRefund(false)
    setIsSolanaScopeInspected(false)
  }, [orderBusinessId, currentEnv, user?.id])

  useEffect(() => {
    if (
      !isOpen
      || !isEvmRefund
      || !user?.id
      || !canonicalRefundResourceId
    ) {
      return
    }
    let cancelled = false
    try {
      const hydrated = evmOperationService.hydratePending(user.id, currentEnv)
      const refundItems = hydrated.items.filter(item =>
        item.operationType === 'evm_platform_meta_refund'
        && item.resourceType === 'refund_order'
        && 'orderId' in item.clientResourceLocator
        && item.clientResourceLocator.orderId === orderBusinessId
      )
      const legacyScopeItems = refundItems.filter(
        item => item.resourceId !== canonicalRefundResourceId
      )
      const latest = refundItems.filter(
        item => item.resourceId === canonicalRefundResourceId
      ).at(-1) ?? null
      setPendingEvmRefund(latest)
      if (hydrated.quarantinedKeys.length > 0 || legacyScopeItems.length > 0) {
        setHasQuarantinedEvmRefund(true)
      }
      if (legacyScopeItems.length > 0) {
        void (async () => {
          let hasUnavailableScope = false
          let hasFoundScope = false
          let hasDualScopeConflict = false
          for (const item of legacyScopeItems) {
            const inspection = await evmOperationService.inspectScopes(
              item,
              canonicalRefundResourceId,
              currentEnv
            )
            hasUnavailableScope ||= inspection.stored.status === 'unavailable'
              || inspection.canonical.status === 'unavailable'
            hasFoundScope ||= inspection.stored.status === 'found'
              || inspection.canonical.status === 'found'
            hasDualScopeConflict ||= inspection.hasConflict
          }
          if (cancelled) {
            return
          }
          setIsEvmRefundPreReservationFailure(false)
          setEvmRefundError(
            hasDualScopeConflict
              ? 'A stored EVM refund key exists in both legacy and canonical scopes. Recovery is frozen.'
              : hasUnavailableScope
                ? 'A legacy EVM refund item could not be verified in both scopes and remains isolated.'
                : hasFoundScope
                  ? 'A legacy EVM refund item has server state under an incompatible scope and remains isolated.'
                  : 'A legacy EVM refund item has no server operation in either scope and remains isolated.'
          )
        })().catch(error => {
          if (!cancelled) {
            setIsEvmRefundPreReservationFailure(false)
            setEvmRefundError(formatEvmOperationError(error))
          }
        })
      } else if (latest) {
        void reconcileEvmRefund(latest).catch(() => undefined)
      } else {
        void loadLatestEvmRefund().catch(() => undefined)
      }
    } catch (error) {
      setEvmRefundError(formatEvmOperationError(error))
    }
    return () => {
      cancelled = true
    }
  }, [
    canonicalRefundResourceId,
    currentEnv,
    isEvmRefund,
    isOpen,
    loadLatestEvmRefund,
    orderBusinessId,
    reconcileEvmRefund,
    user?.id,
  ])

  useEffect(() => {
    if (
      !isOpen
      || !isSolanaRefund
      || !user?.id
      || !canonicalSolanaRefundResourceId
    ) {
      return
    }
    setIsSolanaScopeInspected(false)
    try {
      const hydrated = solanaOperationService.hydratePending(
        user.id,
        currentEnv,
        canonicalSolanaRefundResourceId
      )
      const latest = hydrated.items.at(-1) ?? null
      const hasInvalidScope = hydrated.quarantinedKeys.length > 0
        || hydrated.items.length > 1
      setPendingSolanaRefund(latest)
      setHasQuarantinedSolanaRefund(hasInvalidScope)
      if (hasInvalidScope) {
        setSolanaRefundError(
          hydrated.items.length > 1
            ? 'Multiple Solana refund operations are pending for this order.'
            : 'A stored Solana refund operation is invalid and remains isolated.'
        )
        return
      }
      if (latest) {
        void reconcileSolanaRefund(latest, false).catch(() => undefined)
        return
      }
      void loadNonterminalSolanaRefund().catch(() => undefined)
    } catch (error) {
      setSolanaRefundError(formatSolanaOperationError(error))
    }
  }, [
    canonicalSolanaRefundResourceId,
    currentEnv,
    isOpen,
    isSolanaRefund,
    loadNonterminalSolanaRefund,
    reconcileSolanaRefund,
    user?.id,
  ])

  useEffect(() => {
    if (refundDataError || refundHistoryError) {
      setRecipients({})
      setReason('')
      hasShownExceedsToast.current = false
    }
  }, [refundDataError, refundHistoryError])

  useEffect(() => {
    if (canExecuteRefundWorkflow && refundData?.userPayments) {
      const initialRecipients: Record<string, RecipientStateItem> = {}
      refundData.userPayments.forEach((payment, index) => {
        const key = getPaymentKey(payment, index)
        const availableRaw = availablePerPayment[index] ?? BigInt(decimalToRaw(getPaymentAmountUsd(payment)))
        const formattedAmount = hasKnownTokenDecimals
          ? formatAmountWithDecimals(availableRaw.toString(), tokenDecimals as number, 2)
          : ''
        initialRecipients[key] = {
          address: payment.address,
          transactionHash: payment.transactionHash,
          selected: false,
          inputAmount: formattedAmount,
          maxRawAmount: availableRaw.toString(),
          createdAt: payment.createdAt,
        }
      })
      setRecipients(initialRecipients)
      setReason('')
    }
  }, [
    canExecuteRefundWorkflow,
    refundData?.userPayments,
    getPaymentKey,
    availablePerPayment,
    decimalToRaw,
    getPaymentAmountUsd,
    hasKnownTokenDecimals,
    tokenDecimals,
  ])

  useEffect(() => {
    if (canUseRefundWorkflow && !canExecuteRefundWorkflow) {
      setRecipients({})
      setReason('')
      hasShownExceedsToast.current = false
    }
  }, [canUseRefundWorkflow, canExecuteRefundWorkflow])

  const selectedSummary = useMemo(() => {
    let total = 0n
    const uniqueAddresses = new Set<string>()

    Object.values(recipients).forEach((item) => {
      if (!item.selected) {
        return
      }

      const raw = decimalToRaw(item.inputAmount)
      if (raw === '0') {
        return
      }

      total += BigInt(raw)
      uniqueAddresses.add(normalizeRefundAddressKey(item.address || ''))
    })

    return { total, count: uniqueAddresses.size }
  }, [recipients, decimalToRaw, normalizeRefundAddressKey])

  const hasActiveRecipients = selectedSummary.count > 0

  const formattedTotalRefund = useMemo(() => {
    const sanitizedSymbol = sanitizeTokenSymbol(tokenSymbol)

    if (selectedSummary.total === 0n) {
      return `0.00 ${sanitizedSymbol}`
    }

    return formatRefundAmount(
      selectedSummary.total.toString(),
      chainId || '0',
      tokenAddress,
      tokenSymbol,
      tokenDecimals ?? undefined
    )
  }, [selectedSummary.total, chainId, tokenAddress, tokenSymbol, tokenDecimals])

  const exceedsAvailable = selectedSummary.total > totalAvailableRaw
  const hasNonterminalEvmRefund = isEvmRefund
    && evmRefundOperation !== null
    && !EVM_TERMINAL_STATES.has(evmRefundOperation.state)
  const hasNonterminalSolanaRefund = isSolanaRefund
    && solanaRefundOperation !== null
    && !SOLANA_TERMINAL_STATES.has(solanaRefundOperation.state)

  const isSubmissionDisabled =
    metaRefundMutation.isPending ||
    isSubmittingEvmRefund ||
    isSubmittingSolanaRefund ||
    (isEvmRefund && isRefreshingEvmRefund) ||
    (isEvmRefund && (!user?.id || !canonicalRefundResourceId)) ||
    (isEvmRefund && hasQuarantinedEvmRefund) ||
    (isEvmRefund && (pendingEvmRefund !== null || hasNonterminalEvmRefund)) ||
    (isSolanaRefund && isRefreshingSolanaRefund) ||
    (isSolanaRefund && (!user?.id || !canonicalSolanaRefundResourceId)) ||
    (isSolanaRefund && !isSolanaScopeInspected) ||
    (isSolanaRefund && hasQuarantinedSolanaRefund) ||
    (isSolanaRefund && solanaRefundError !== null) ||
    (isSolanaRefund && (
      pendingSolanaRefund !== null || hasNonterminalSolanaRefund
    )) ||
    !canExecuteRefundWorkflow ||
    !hasKnownTokenDecimals ||
    !hasAvailableRefund ||
    exceedsAvailable

  useEffect(() => {
    if (!hasActiveRecipients) {
      hasShownExceedsToast.current = false
      return
    }

    if (exceedsAvailable) {
      if (!hasShownExceedsToast.current) {
        hasShownExceedsToast.current = true
        showToastError({
          title: t('contractRegistry.metaRefund.errors.exceedsAvailable', 'Selected refund total {{selected}} exceeds available balance {{available}}.', {
            selected: formattedTotalRefund,
            available: formattedTotalAvailable,
          }),
          id: 'meta-refund-total-exceeds',
        })
      }
    } else {
      hasShownExceedsToast.current = false
    }
  }, [exceedsAvailable, hasActiveRecipients, formattedTotalRefund, formattedTotalAvailable, showToastError, t])

  const submitTooltipLabel = useMemo(() => {
    if (isRefundWorkflowLoading) {
      return t('contractRegistry.metaRefund.errors.loading', 'Refund data is still loading.')
    }
    if (isRefundUnsupported) {
      return refundUnsupportedMessage
    }
    if (refundDataLoadFailed) {
      return t('contractRegistry.metaRefund.errors.loadRefundData', 'Unable to load refund data. Please try again.')
    }
    if (refundHistoryLoadFailed) {
      return t('contractRegistry.metaRefund.errors.loadRefundHistory', 'Unable to load refund history. Please try again.')
    }
    if (isEvmRefund && (!user?.id || !canonicalRefundResourceId)) {
      return 'Canonical EVM refund identity or persistent Admin identity is unavailable.'
    }
    if (isEvmRefund && hasQuarantinedEvmRefund) {
      return 'A stored EVM refund operation is isolated and must be resolved before submitting.'
    }
    if (isEvmRefund && (pendingEvmRefund || hasNonterminalEvmRefund)) {
      return 'An EVM refund operation is still pending. Recover it before starting another action.'
    }
    if (isSolanaRefund && (!user?.id || !canonicalSolanaRefundResourceId)) {
      return 'Canonical Solana refund identity or persistent Admin identity is unavailable.'
    }
    if (isSolanaRefund && !isSolanaScopeInspected) {
      return 'Solana refund operation scope has not been inspected successfully.'
    }
    if (isSolanaRefund && hasQuarantinedSolanaRefund) {
      return 'A stored Solana refund operation is isolated and must be resolved first.'
    }
    if (isSolanaRefund && solanaRefundError) {
      return solanaRefundError
    }
    if (isSolanaRefund && (pendingSolanaRefund || hasNonterminalSolanaRefund)) {
      return 'A Solana refund operation is still pending. Recover it before another action.'
    }
    if (canUseRefundWorkflow && !canExecuteRefundWorkflow) {
      return resolvedRefundDisabledMessage
    }
    if (!hasKnownTokenDecimals) {
      return t(
        'contractRegistry.metaRefund.errors.tokenMetadataMissing',
        'Token decimals are unavailable for this order. Meta Refund is disabled until refund metadata is complete.'
      )
    }
    if (!hasActiveRecipients) {
      return t('contractRegistry.metaRefund.errors.noRecipients', 'Please select at least one payer to refund.')
    }
    if (exceedsAvailable) {
      return t('contractRegistry.metaRefund.errors.exceedsAvailable', 'Selected refund total {{selected}} exceeds available balance {{available}}.', {
        selected: formattedTotalRefund,
        available: formattedTotalAvailable,
      })
    }
    if (!hasAvailableRefund) {
      return t('contractRegistry.metaRefund.errors.noAvailable', 'No refundable balance remains for this order.')
    }
    return undefined
  }, [
    isRefundWorkflowLoading,
    isRefundUnsupported,
    refundUnsupportedMessage,
    refundDataLoadFailed,
    refundHistoryLoadFailed,
    isEvmRefund,
    user?.id,
    canonicalRefundResourceId,
    hasQuarantinedEvmRefund,
    pendingEvmRefund,
    hasNonterminalEvmRefund,
    isSolanaRefund,
    canonicalSolanaRefundResourceId,
    isSolanaScopeInspected,
    hasQuarantinedSolanaRefund,
    solanaRefundError,
    pendingSolanaRefund,
    hasNonterminalSolanaRefund,
    canUseRefundWorkflow,
    canExecuteRefundWorkflow,
    resolvedRefundDisabledMessage,
    hasActiveRecipients,
    hasKnownTokenDecimals,
    exceedsAvailable,
    hasAvailableRefund,
    formattedTotalRefund,
    formattedTotalAvailable,
    t,
  ])

  const handleToggleRecipient = (key: string) => {
    setRecipients((prev) => {
      const current = prev[key]
      if (!current) {
        return prev
      }
      if (BigInt(current.maxRawAmount) <= 0n) {
        return prev
      }
      return {
        ...prev,
        [key]: {
          ...current,
          selected: !current.selected,
        },
      }
    })
  }

  const handleAmountChange = (key: string, value: string) => {
    const current = recipients[key]
    if (!current) {
      return
    }

    setRecipients((prev) => {
      const next = prev[key]
      if (!next) {
        return prev
      }
      return {
        ...prev,
        [key]: {
          ...next,
          inputAmount: value,
        },
      }
    })

    const rawAmountStr = decimalToRaw(value)
    if (
      current.selected &&
      rawAmountStr !== '0' &&
      (() => {
        try {
          return BigInt(rawAmountStr) > BigInt(current.maxRawAmount)
        } catch {
          return false
        }
      })()
    ) {
      showToastError({
        title: t('contractRegistry.metaRefund.errors.exceedsPaid', 'Refund amount cannot exceed the amount paid.'),
        id: `meta-refund-amount-exceeds-${key}`,
      })
    }
  }

  const handleSubmit = async () => {
    if (!orderBusinessId) {
      return
    }
    if (isRefundUnsupported) {
      showToastError({
        title: refundUnsupportedMessage,
        id: 'meta-refund-unsupported-chain',
      })
      return
    }
    if (isRefundWorkflowLoading || refundDataLoadFailed || refundHistoryLoadFailed || !refundData) {
      showToastError({
        title: refundHistoryLoadFailed
          ? t('contractRegistry.metaRefund.errors.loadRefundHistory', 'Unable to load refund history. Please try again.')
          : t('contractRegistry.metaRefund.errors.loadRefundData', 'Unable to load refund data. Please try again.'),
        id: 'meta-refund-data-unavailable',
      })
      return
    }
    if (!canExecuteRefundWorkflow) {
      showToastError({
        title:
          resolvedRefundDisabledMessage ||
          t(
            'contractRegistry.metaRefund.errors.refundUnavailable',
            'Meta Refund is not available for this order. Only refund history is available.'
          ),
        id: 'meta-refund-disabled',
      })
      return
    }
    if (!hasKnownTokenDecimals) {
      showToastError({
        title: t(
          'contractRegistry.metaRefund.errors.tokenMetadataMissing',
          'Token decimals are unavailable for this order. Meta Refund is disabled until refund metadata is complete.'
        ),
        id: 'meta-refund-token-metadata-missing',
      })
      return
    }
    if (!hasAvailableRefund) {
      showToastError({
        title: t('contractRegistry.metaRefund.errors.noAvailable', 'No refundable balance remains for this order.'),
        id: 'meta-refund-no-available',
      })
      return
    }
    if (isEvmRefund && hasQuarantinedEvmRefund) {
      showToastError({
        title: 'A stored EVM refund operation is isolated and must be resolved first.',
        id: 'meta-refund-operation-isolated',
      })
      return
    }
    if (
      isSolanaRefund
      && (
        !isSolanaScopeInspected
        || hasQuarantinedSolanaRefund
        || solanaRefundError !== null
        || pendingSolanaRefund !== null
        || hasNonterminalSolanaRefund
      )
    ) {
      showToastError({
        title: 'Resolve the current Solana refund operation before submitting.',
        id: 'meta-refund-solana-operation-unavailable',
      })
      return
    }

    const reasonValidation = validateTextWithConstraint(reason, REFUND_REASON_CONSTRAINT, {
      requiredOverride: true
    })

    if (!reasonValidation.valid || !reasonValidation.value) {
      const message = reasonValidation.error
        ? t('contractRegistry.metaRefund.errors.reasonInvalid', {
            message: reasonValidation.error,
          })
        : t('contractRegistry.metaRefund.errors.reasonTooShort', {
            count: REASON_MIN_LENGTH,
          })

      showToastError({
        title: message,
        id: 'meta-refund-reason-invalid',
      })
      return
    }

    const sanitizedReason = reasonValidation.value

    const aggregated = new Map<string, { raw: bigint; original: string }>()

    for (const item of Object.values(recipients)) {
      if (!item.selected) {
        continue
      }

      const rawAmountStr = decimalToRaw(item.inputAmount)
      if (rawAmountStr === '0') {
        showToastError({
          title: t('contractRegistry.metaRefund.errors.invalidAmount', 'Refund amount must be greater than 0.'),
          id: 'meta-refund-amount-invalid',
        })
        return
      }
      const rawAmount = BigInt(rawAmountStr)
      const normalizedAddress = normalizeRefundAddressKey(item.address || '')

      if (rawAmount > BigInt(item.maxRawAmount)) {
        showToastError({
          title: t('contractRegistry.metaRefund.errors.exceedsPaid', 'Refund amount cannot exceed the amount paid.'),
          id: `meta-refund-amount-exceeds-${normalizedAddress}`,
        })
        return
      }

      const existing = aggregated.get(normalizedAddress)

      if (existing) {
        aggregated.set(normalizedAddress, {
          original: existing.original,
          raw: existing.raw + rawAmount,
        })
      } else {
        aggregated.set(normalizedAddress, {
          original: item.address,
          raw: rawAmount,
        })
      }
    }

    if (aggregated.size === 0) {
      showToastError({
        title: t('contractRegistry.metaRefund.errors.noRecipients', 'Please select at least one payer to refund.'),
        id: 'meta-refund-no-recipient',
      })
      return
    }

    let aggregatedTotal = 0n
    aggregated.forEach((entry) => {
      aggregatedTotal += entry.raw
    })

    if (aggregatedTotal > totalAvailableRaw) {
      showToastError({
        title: t('contractRegistry.metaRefund.errors.exceedsAvailable', 'Selected refund total {{selected}} exceeds available balance {{available}}.', {
          selected: formatRefundAmount(
            aggregatedTotal.toString(),
            chainId || '0',
            tokenAddress,
            tokenSymbol,
            tokenDecimals ?? undefined
          ),
          available: formattedTotalAvailable,
        }),
        id: 'meta-refund-total-exceeds',
      })
      return
    }

    const aggregatedEntries = Array.from(aggregated.values())
    const chosenRecipients = aggregatedEntries.map((entry) => entry.original)
    const chosenAmounts = aggregatedEntries.map((entry) => entry.raw.toString())
    const requestPayload = {
      recipients: chosenRecipients,
      amounts: chosenAmounts,
      reason: sanitizedReason,
    }

    const fallbackMessage = t('contractRegistry.metaRefund.toast.errorDescription', 'Meta Refund execution failed. Please try again later.')
    let submittedEvmIdempotencyKey: string | null = null
    let submittedSolanaIdempotencyKey: string | null = null
    let submittedSolanaExecutionEpoch: number | null = null

    try {
      if (isEvmRefund) {
        if (!user?.id || !canonicalRefundResourceId) {
          throw new Error('Canonical EVM refund operation identity is unavailable')
        }
        if (hasNonterminalEvmRefund && !pendingEvmRefund) {
          throw new Error(
            'An EVM refund operation was recovered from the server. Resolve it before starting another action.'
          )
        }
        if (
          pendingEvmRefund
          && (
            pendingEvmRefund.operationType !== 'evm_platform_meta_refund'
            || pendingEvmRefund.resourceType !== 'refund_order'
            || pendingEvmRefund.resourceId !== canonicalRefundResourceId
            || JSON.stringify(pendingEvmRefund.requestBody) !== JSON.stringify(requestPayload)
            || !('orderId' in pendingEvmRefund.clientResourceLocator)
            || pendingEvmRefund.clientResourceLocator.orderId !== orderBusinessId
          )
        ) {
          throw new Error(
            'Another EVM refund operation is still pending. Resolve it before starting a new action.'
          )
        }
        setIsSubmittingEvmRefund(true)
        const pending = pendingEvmRefund
          ?? refundService.createEvmMetaRefundOperation(
            user.id,
            orderBusinessId,
            canonicalRefundResourceId,
            requestPayload,
            currentEnv
          )
        submittedEvmIdempotencyKey = pending.idempotencyKey
        setPendingEvmRefund(pending)
        setEvmRefundOperation(null)
        setEvmRefundError(null)
        setIsEvmRefundPreReservationFailure(false)
        const operation = await refundService.submitEvmMetaRefundOperation(
          user.id,
          pending,
          currentEnv
        )
        setEvmRefundOperation(operation)
        setEvmRefundError(null)
        setIsEvmRefundPreReservationFailure(false)
        syncPendingEvmRefund(pending.idempotencyKey)
        const description = operation.txHash
          ? `Operation ID: ${operation.operationId}\nTx Hash: ${operation.txHash}`
          : `Operation ID: ${operation.operationId}`
        if (operation.state === 'rejected' || operation.state === 'failed') {
          showToastError({
            title: `EVM refund operation ${operation.state}`,
            description: operation.error?.message ?? description,
          })
        } else if (operation.state === 'unknown') {
          showToastWarning({
            title: 'EVM refund operation is frozen',
            description: operation.error?.message ?? description,
          })
        } else {
          showToastSuccess({
            title: `EVM refund operation ${operation.state}`,
            description,
          })
        }
        if (EVM_TERMINAL_STATES.has(operation.state)) {
          void Promise.all([refetchRefundData(), refetchRefundHistory()])
        }
        return
      }
      if (isSolanaRefund) {
        if (!user?.id || !canonicalSolanaRefundResourceId) {
          throw new Error('Canonical Solana refund operation identity is unavailable')
        }
        if (!isSolanaScopeInspected) {
          throw new Error('Solana refund operation scope has not been inspected')
        }
        if (hasNonterminalSolanaRefund && !pendingSolanaRefund) {
          throw new Error(
            'A Solana refund operation was recovered from the server. Resolve it first.'
          )
        }
        const executionEpoch = solanaExecutionEpochRef.current
        submittedSolanaExecutionEpoch = executionEpoch
        setIsSubmittingSolanaRefund(true)
        const pending = refundService.createSolanaMetaRefundOperation(
          user.id,
          orderBusinessId,
          canonicalSolanaRefundResourceId,
          requestPayload,
          currentEnv
        )
        submittedSolanaIdempotencyKey = pending.idempotencyKey
        setPendingSolanaRefund(pending)
        setSolanaRefundOperation(null)
        setSolanaRefundError(null)
        const operation = await refundService.submitSolanaMetaRefundOperation(
          user.id,
          pending,
          currentEnv
        )
        if (executionEpoch !== solanaExecutionEpochRef.current) {
          return
        }
        setSolanaRefundOperation(operation)
        setSolanaRefundError(null)
        syncPendingSolanaRefund(pending.idempotencyKey)
        const description = operation.signature
          ? `Operation ID: ${operation.operationId}\nSignature: ${operation.signature}`
          : `Operation ID: ${operation.operationId}`
        if (operation.state === 'rejected' || operation.state === 'failed') {
          showToastError({
            title: `Solana refund operation ${operation.state}`,
            description: operation.error?.message ?? description,
          })
        } else if (operation.state === 'unknown') {
          showToastWarning({
            title: 'Solana refund operation is reconciling',
            description: operation.error?.message ?? description,
          })
        } else {
          showToastSuccess({
            title: `Solana refund operation ${operation.state}`,
            description,
          })
        }
        if (SOLANA_TERMINAL_STATES.has(operation.state)) {
          await Promise.all([refetchRefundData(), refetchRefundHistory()])
        }
        return
      }
      const result = await metaRefundMutation.mutateAsync({
        orderId: orderBusinessId,
        payload: requestPayload,
      })

      if (!result?.success) {
        const errorMessage = result?.message || result?.error || fallbackMessage
        const descriptionParts: string[] = []
        if (result?.error && result.error !== errorMessage) {
          descriptionParts.push(result.error)
        }
        if (result?.errorCode) {
          descriptionParts.push(`Code: ${result.errorCode}`)
        }
        if (result?.rawReason && result.rawReason !== result.error && result.rawReason !== result.message) {
          descriptionParts.push(`Raw reason: ${result.rawReason}`)
        }
        if (result?.txHash) {
          descriptionParts.push(`Tx Hash: ${result.txHash}`)
        }

        showToastError({
          title: errorMessage,
          description: descriptionParts.length ? descriptionParts.join('\n') : undefined,
          id: 'meta-refund-submit-error'
        })
        return
      }

      showToastSuccess({
        title: result.message || t('contractRegistry.metaRefund.toast.successTitle', 'Meta Refund submitted'),
        description: result.txHash
          ? `Tx Hash: ${result.txHash}`
          : t('contractRegistry.metaRefund.toast.successDescription', 'The Meta Refund transaction has been broadcast. It will appear in refund records shortly.'),
        duration: 5000,
        isClosable: true,
      })

      // Reset local selection state before closing to avoid residual exceedsAvailable warnings
      setRecipients({})
      setReason('')
      hasShownExceedsToast.current = false

      refetchRefundData()
      refetchRefundHistory()
      onClose()
    } catch (error: unknown) {
      if (submittedEvmIdempotencyKey) {
        try {
          syncPendingEvmRefund(submittedEvmIdempotencyKey)
        } catch (storageError) {
          setEvmRefundError(formatEvmOperationError(storageError))
        }
      }
      if (
        submittedSolanaIdempotencyKey
        && submittedSolanaExecutionEpoch === solanaExecutionEpochRef.current
      ) {
        try {
          syncPendingSolanaRefund(submittedSolanaIdempotencyKey)
        } catch (storageError) {
          setSolanaRefundError(formatSolanaOperationError(storageError))
        }
      }
      const apiErrorRaw = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error

      const normalizeMessage = (input: unknown): string | undefined => {
        if (!input) {
          return undefined
        }
        if (typeof input === 'string') {
          return input
        }
        if (input instanceof Error) {
          return input.message
        }
        if (typeof input === 'object') {
          const maybe = input as { message?: unknown; code?: unknown; detail?: unknown }
          if (typeof maybe.message === 'string') {
            return maybe.message
          }
          if (typeof maybe.detail === 'string') {
            return maybe.detail
          }
          if (typeof maybe.code === 'string') {
            return maybe.code
          }
        }
        return undefined
      }

      const normalizedError =
        normalizeMessage(apiErrorRaw) || normalizeMessage(error) || fallbackMessage

      if (submittedEvmIdempotencyKey) {
        setEvmRefundError(normalizedError)
        setIsEvmRefundPreReservationFailure(
          evmOperationService.isPreReservationError(error)
        )
      }
      if (
        submittedSolanaIdempotencyKey
        && submittedSolanaExecutionEpoch === solanaExecutionEpochRef.current
      ) {
        setSolanaRefundError(normalizedError)
        setIsSolanaScopeInspected(false)
      }
      if (
        submittedSolanaExecutionEpoch === null
        || submittedSolanaExecutionEpoch === solanaExecutionEpochRef.current
      ) {
        showToastError({ title: normalizedError, id: 'meta-refund-submit-error' })
      }
    } finally {
      setIsSubmittingEvmRefund(false)
      if (
        submittedSolanaExecutionEpoch === null
        || submittedSolanaExecutionEpoch === solanaExecutionEpochRef.current
      ) {
        setIsSubmittingSolanaRefund(false)
      }
    }
  }

  const renderHistoryRow = (
    entry: RefundHistoryEntry,
    index: number,
    source: RefundHistorySource
  ) => {
    const explorerUrl = entry.txHash ? getExplorerTxUrl(chainId || '0', entry.txHash) : undefined
    const metaEntries = Array.isArray(entry.refund_meta)
      ? entry.refund_meta
      : Array.isArray(entry.refundMeta)
        ? entry.refundMeta
        : []
    const historyKey = entry.txHash || entry.transaction_hash || `history-${index}`
    const isPlatform = source === 'platform'

    return (
      <Tr key={historyKey}>
        <Td>
          {entry.txHash ? (
            <HStack spacing={2} align="center">
              <Text fontSize="sm" fontFamily="mono" noOfLines={1} maxW="220px">
                {entry.txHash}
              </Text>
              <Tooltip label={t('contractRegistry.metaRefund.actions.copy', 'Copy transaction hash')}>
                <IconButton
                  aria-label="copy"
                  size="xs"
                  icon={<CopyIcon />}
                  variant="ghost"
                  onClick={() => navigator.clipboard.writeText(entry.txHash!)}
                />
              </Tooltip>
              {explorerUrl && (
                <Tooltip label={t('contractRegistry.metaRefund.actions.viewExplorer', 'Open in explorer')}>
                  <IconButton
                    aria-label="open"
                    size="xs"
                    icon={<ExternalLinkIcon />}
                    variant="ghost"
                    onClick={() => window.open(explorerUrl, '_blank')}
                  />
                </Tooltip>
              )}
            </HStack>
          ) : (
            <Text fontSize="sm" color={mutedText}>
              {t('contractRegistry.metaRefund.history.noTx', 'No on-chain hash recorded')}
            </Text>
          )}
        </Td>
        <Td>
          {formatRefundAmount(
            decimalToRaw(entry.refundAmountUsd || '0'),
            chainId || '0',
            tokenAddress,
            tokenSymbol,
            tokenDecimals ?? undefined
          )}
        </Td>
        <Td>
          <Stack spacing={1} align="flex-start">
            {metaEntries.map((meta, metaIndex) => (
              <Text key={`${meta.recipient ?? 'unknown'}-${metaIndex}`} fontSize="xs">
                {meta.recipient} · {(() => {
                  const rawAmount =
                    meta.amountUsd || meta.amount_usd
                      ? decimalToRaw(meta.amountUsd ?? meta.amount_usd ?? '0')
                      : meta.amount ?? '0'

                  return formatRefundAmount(
                    rawAmount,
                    chainId || '0',
                    tokenAddress,
                    tokenSymbol,
                    tokenDecimals ?? undefined
                  )
                })()}
              </Text>
            ))}
          </Stack>
        </Td>
        <Td>
          <HStack spacing={2} align="center">
            <Badge colorScheme={entry.isVerified ? 'green' : 'yellow'}>
              {entry.isVerified
                ? t('contractRegistry.metaRefund.history.verified', 'Verified')
                : t('contractRegistry.metaRefund.history.pending', 'Pending verification')}
            </Badge>
            <Badge colorScheme={isPlatform ? 'purple' : 'blue'}>
              {isPlatform
                ? t('contractRegistry.metaRefund.history.platformBadge', 'Platform')
                : t('contractRegistry.metaRefund.history.merchantBadge', 'Merchant')}
            </Badge>
          </HStack>
        </Td>
        <Td>
          <Stack spacing={0}>
            <Text fontSize="sm">{entry.createdAt ? formatDateTime(entry.createdAt) : t('common.na', 'N/A')}</Text>
            {entry.createdAt && (
              <Text fontSize="xs" color={mutedText}>
                {formatRelativeTime(entry.createdAt)}
              </Text>
            )}
          </Stack>
        </Td>
      </Tr>
    )
  }

  const refundHistoryRows = useMemo<RefundHistoryRow[]>(() => {
    const rows = refundHistory?.refundHistory?.flatMap((entry) => {
      const source = getRefundHistorySource(entry)
      return source ? [{ entry, source }] : []
    }) ?? []

    return rows.sort((left, right) => {
      return getRefundHistorySortTime(left.entry) - getRefundHistorySortTime(right.entry)
    })
  }, [refundHistory?.refundHistory])

  const merchantRefundHistoryCount = refundHistoryRows.filter(
    (row) => row.source === 'merchant'
  ).length
  const platformRefundHistoryCount = refundHistoryRows.filter(
    (row) => row.source === 'platform'
  ).length

  const invalidRefundHistoryCount = useMemo(() => {
    return refundHistory?.refundHistory?.filter((entry) => {
      return getRefundHistorySource(entry) === null
    }).length ?? 0
  }, [refundHistory?.refundHistory])

  const showHistory = refundHistoryRows.length > 0
  const hasInvalidRefundHistory = invalidRefundHistoryCount > 0
  const handleRefreshRefundWorkflow = useCallback(async () => {
    await Promise.all([refetchRefundData(), refetchRefundHistory()])
  }, [refetchRefundData, refetchRefundHistory])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg={modalHeaderBg} borderBottomWidth="1px" pr={12}>
          <HStack spacing={3} align="center">
            <FiRotateCcw />
            <Text>{t('contractRegistry.metaRefund.title', 'Meta Refund')}</Text>
            {refundData?.metaRefundDisabledReason === 'NO_REFUNDABLE_BALANCE' && (
              <Badge colorScheme="purple">
                {t('contractRegistry.metaRefund.status.completed', 'Refund completed')}
              </Badge>
            )}
            <Spacer />
            {shouldLoadRefundData && (
              <RefreshIconButton
                label={t('common.refresh')}
                tooltipPlacement="left"
                onClick={() => void handleRefreshRefundWorkflow()}
                isLoading={isRefreshingRefundWorkflow}
                isDisabled={isRefreshingRefundWorkflow}
                flexShrink={0}
              />
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={6}>
            <Box>
              <Text fontSize="sm" color={mutedText}>
                {t(
                  'contractRegistry.metaRefund.description',
                  'Execute a platform Meta Refund from the order on-chain operational address.'
                )}
              </Text>
              <Flex mt={3} gap={4} flexWrap="wrap">
                <Stack spacing={0}>
                  <Text fontSize="xs" color={mutedText}>
                    {t('contractRegistry.metaRefund.labels.orderId', 'Order ID')}
                  </Text>
                  <Text fontWeight="medium">{orderBusinessId || t('common.na', 'N/A')}</Text>
                </Stack>
                {deploymentAddressRows.map(item => (
                  <Stack spacing={0} key={item.kind}>
                    <Text fontSize="xs" color={mutedText}>
                      {deploymentAddressLabels[item.kind]}
                    </Text>
                    <HStack>
                      <Text fontWeight="medium" fontFamily="mono">
                        {item.address}
                      </Text>
                      <Tooltip label={t('contractRegistry.metaRefund.actions.copy', 'Copy')}>
                        <IconButton
                          aria-label="copy-address"
                          icon={<CopyIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => navigator.clipboard.writeText(item.address)}
                        />
                      </Tooltip>
                      <Tooltip label={t('contractRegistry.metaRefund.actions.viewExplorer', 'Open in explorer')}>
                        <IconButton
                          aria-label="explorer-address"
                          icon={<ExternalLinkIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => {
                            const url = getExplorerAddressUrl(chainId || '0', item.address)
                            if (url) {
                              window.open(url, '_blank')
                            }
                          }}
                        />
                      </Tooltip>
                    </HStack>
                  </Stack>
                ))}
                {deploymentAddressRows.length === 0 && (
                  <Stack spacing={0}>
                    <Text fontSize="xs" color={mutedText}>
                      {t('contractRegistry.table.paymentAddress', 'Payment Address')}
                    </Text>
                    <Text fontWeight="medium">
                      {t('contractRegistry.metaRefund.labels.contractPending', 'Not deployed or address missing')}
                    </Text>
                  </Stack>
                )}
                {refundData?.windowEndsAt && (
                  <Stack spacing={0}>
                    <Text fontSize="xs" color={mutedText}>
                      {t('contractRegistry.metaRefund.labels.refundWindow', 'Refund window closes at')}
                    </Text>
                    <Text fontWeight="medium">{formatDateTime(refundData.windowEndsAt)}</Text>
                  </Stack>
                )}
              </Flex>
            </Box>

            {isRefundUnsupported && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{refundUnsupportedMessage}</AlertDescription>
              </Alert>
            )}

            {refundDataLoadFailed && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Stack spacing={2} align="flex-start">
                  <AlertDescription>
                    {t('contractRegistry.metaRefund.errors.loadRefundData', 'Unable to load refund data. Please try again.')}
                  </AlertDescription>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void refetchRefundData()}
                    isLoading={isFetchingRefundData}
                  >
                    {t('common.retry', 'Retry')}
                  </Button>
                </Stack>
              </Alert>
            )}

            {refundHistoryLoadFailed && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Stack spacing={2} align="flex-start">
                  <AlertDescription>
                    {t('contractRegistry.metaRefund.errors.loadRefundHistory', 'Unable to load refund history. Please try again.')}
                  </AlertDescription>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void refetchRefundHistory()}
                    isLoading={isFetchingHistory}
                  >
                    {t('common.retry', 'Retry')}
                  </Button>
                </Stack>
              </Alert>
            )}

            {isEvmRefund && (
              pendingEvmRefund
              || evmRefundOperation
              || evmRefundError
              || hasQuarantinedEvmRefund
              || isRefreshingEvmRefund
            ) && (
              <Alert
                status={
                  hasQuarantinedEvmRefund || evmRefundError
                    ? 'warning'
                    : evmRefundOperation?.state === 'completed'
                    ? 'success'
                    : evmRefundOperation?.state === 'failed'
                      || evmRefundOperation?.state === 'rejected'
                      ? 'error'
                      : 'warning'
                }
                borderRadius="md"
              >
                <AlertIcon />
                <Stack spacing={2} align="flex-start" width="100%">
                  <AlertDescription>
                    <Text fontWeight="semibold">
                      EVM execution operation: {
                        evmRefundOperation?.state
                        ?? (isEvmRefundPreReservationFailure
                          ? 'not reserved'
                          : hasQuarantinedEvmRefund
                            ? 'recovery isolated'
                            : 'pending')
                      }
                    </Text>
                    <Text fontSize="sm">
                      {evmRefundError
                        ?? (hasQuarantinedEvmRefund
                          ? 'A stored EVM operation is invalid and has been isolated. It was not submitted.'
                          : evmRefundOperation
                            ? `Operation ${evmRefundOperation.operationId}.`
                            : 'The persisted operation is pending automatic processing.')}
                      {' '}This execution state is separate from the refund and order business states.
                    </Text>
                  </AlertDescription>
                  {(
                    pendingEvmRefund
                    || hasNonterminalEvmRefund
                    || evmRefundError
                    || hasQuarantinedEvmRefund
                  ) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void refreshEvmRefund().catch(() => undefined)}
                      isLoading={isRefreshingEvmRefund}
                      isDisabled={isRefreshingEvmRefund || !canonicalRefundResourceId}
                    >
                      Refresh EVM operation
                    </Button>
                  )}
                </Stack>
              </Alert>
            )}

            {isSolanaRefund && (
              pendingSolanaRefund
              || solanaRefundOperation
              || solanaRefundError
              || hasQuarantinedSolanaRefund
              || isRefreshingSolanaRefund
              || !isSolanaScopeInspected
            ) && (
              <Alert
                status={
                  hasQuarantinedSolanaRefund || solanaRefundError
                    ? 'warning'
                    : solanaRefundOperation?.state === 'completed'
                      ? 'success'
                      : solanaRefundOperation?.state === 'failed'
                        || solanaRefundOperation?.state === 'rejected'
                        ? 'error'
                        : 'warning'
                }
                borderRadius="md"
              >
                <AlertIcon />
                <Stack spacing={2} align="flex-start" width="100%">
                  <AlertDescription>
                    <Text fontWeight="semibold">
                      Solana execution operation: {
                        solanaRefundOperation?.state
                        ?? (hasQuarantinedSolanaRefund ? 'recovery isolated' : 'pending')
                      }
                    </Text>
                    <Text fontSize="sm">
                      {solanaRefundError
                        ?? (hasQuarantinedSolanaRefund
                          ? 'A stored Solana operation is invalid and remains isolated.'
                          : solanaRefundOperation
                            ? `Operation ${solanaRefundOperation.operationId}.`
                            : 'The persisted operation is pending automatic processing.')}
                      {' '}This execution state is separate from the refund and order states.
                    </Text>
                  </AlertDescription>
                  {!hasQuarantinedSolanaRefund && (
                    pendingSolanaRefund
                    || hasNonterminalSolanaRefund
                    || solanaRefundError
                    || !isSolanaScopeInspected
                  ) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void refreshSolanaRefund().catch(() => undefined)}
                      isLoading={isRefreshingSolanaRefund}
                      isDisabled={
                        isRefreshingSolanaRefund || !canonicalSolanaRefundResourceId
                      }
                    >
                      {pendingSolanaRefund && !pendingSolanaRefund.lastAttemptAt
                        ? 'Resume Solana operation'
                        : 'Refresh Solana operation'}
                    </Button>
                  )}
                </Stack>
              </Alert>
            )}

            {shouldLoadRefundData && (
              <Box mb={3}>
                <HStack mb={2} spacing={2}>
                  <WarningTwoIcon />
                  <Text fontWeight="medium">
                    {t(
                      'contractRegistry.metaRefund.history.allTitleWithCounts',
                      'Refund History ({{merchantCount}}/{{platformCount}})',
                      {
                        merchantCount: merchantRefundHistoryCount,
                        platformCount: platformRefundHistoryCount,
                      }
                    )}
                  </Text>
                </HStack>
                {isLoadingHistory ? (
                  <Skeleton height="160px" borderRadius="md" />
                ) : canRenderRefundHistory && (showHistory || hasInvalidRefundHistory) ? (
                  <Stack spacing={3}>
                    {hasInvalidRefundHistory && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                          {t(
                            'contractRegistry.metaRefund.history.dataIntegrityErrorWithCount',
                            'Refund history contains {{count}} record(s) with invalid refund source metadata. Those records are hidden until repaired.',
                            { count: invalidRefundHistoryCount }
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    {showHistory && (
                      <Box overflowX="auto">
                        <Table
                          size="sm"
                          variant="striped"
                          sx={{
                            'th, td': {
                              px: 3,
                              py: 2,
                            },
                          }}
                        >
                          <Thead>
                            <Tr>
                              <Th>{t('contractRegistry.metaRefund.history.columns.txHash', 'Transaction')}</Th>
                              <Th>{t('contractRegistry.metaRefund.history.columns.total', 'Total amount')}</Th>
                              <Th>{t('contractRegistry.metaRefund.history.columns.recipients', 'Refund breakdown')}</Th>
                              <Th>{t('contractRegistry.metaRefund.history.columns.status', 'Status')}</Th>
                              <Th>{t('contractRegistry.metaRefund.history.columns.created', 'Created at')}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {refundHistoryRows.map((row, historyIndex) =>
                              renderHistoryRow(row.entry, historyIndex, row.source)
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </Stack>
                ) : canRenderRefundHistory ? (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                      {t('contractRegistry.metaRefund.history.empty', 'No Meta Refund records yet.')}
                    </AlertDescription>
                  </Alert>
                ) : null}
              </Box>
            )}

            {isRefundWorkflowLoading ? (
              <Stack spacing={4}>
                <Skeleton height="160px" borderRadius="md" />
                <Skeleton height="120px" borderRadius="md" />
                <Skeleton height="200px" borderRadius="md" />
              </Stack>
            ) : canUseRefundWorkflow ? (
              <>
            {!canExecuteRefundWorkflow ? (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  {resolvedRefundDisabledMessage ||
                    t(
                      'contractRegistry.metaRefund.errors.refundUnavailable',
                      'Meta Refund is not available for this order. Only refund history is available.'
                    )}
                </AlertDescription>
              </Alert>
            ) : (
              <>
            <Box borderWidth="1px" borderRadius="md" borderColor={summaryBorder} bg={summaryBg} p={4}>
              <HStack spacing={4} align="center">
                <InfoOutlineIcon />
                <Stack spacing={0}>
                  <Text fontWeight="medium">
                    {t('contractRegistry.metaRefund.summary.title', 'Refund summary')}
                  </Text>
                  <Text fontSize="sm" color={mutedText}>
                    {t('contractRegistry.metaRefund.summary.description', 'Selected {{count}} payers, total {{amount}}', {
                      count: selectedSummary.count,
                      amount: formattedTotalRefund,
                    })}
                  </Text>
                  <Text fontSize="sm" color={mutedText}>
                    {t('contractRegistry.metaRefund.summary.totalAvailable', 'Available for refund: {{amount}}', {
                      amount: formattedTotalAvailable,
                    })}
                  </Text>
                  {exceedsAvailable && (
                    <Text fontSize="sm" color="red.400">
                      {t('contractRegistry.metaRefund.errors.exceedsAvailable', 'Selected refund total {{selected}} exceeds available balance {{available}}.', {
                        selected: formattedTotalRefund,
                        available: formattedTotalAvailable,
                      })}
                    </Text>
                  )}
                </Stack>
                <Spacer />
                <Button
                  size="sm"
                  onClick={handleSelectAll}
                  leftIcon={<CheckIcon />}
                  isDisabled={!refundData?.userPayments?.length || !hasKnownTokenDecimals}
                >
                  {t('contractRegistry.metaRefund.actions.selectAll', 'Full refund')}
                </Button>
                <Button size="sm" onClick={handleClearSelection} variant="ghost" isDisabled={!recipients || Object.keys(recipients).length === 0}>
                  {t('contractRegistry.metaRefund.actions.clear', 'Clear selection')}
                </Button>
              </HStack>
            </Box>

            <Box>
              <Text fontWeight="medium" mb={3}>
                {t('contractRegistry.metaRefund.payers.title', 'Payer list')}
              </Text>
              {showTokenMetadataMissing && (
                <Alert status="warning" borderRadius="md" mb={4}>
                  <AlertIcon />
                  <AlertDescription>
                    {t(
                      'contractRegistry.metaRefund.errors.tokenMetadataMissing',
                      'Token decimals are unavailable for this order. Meta Refund is disabled until refund metadata is complete.'
                    )}
                  </AlertDescription>
                </Alert>
              )}
              {refundData?.userPayments && refundData.userPayments.length > 0 ? (
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th>{t('contractRegistry.metaRefund.payers.columns.select', 'Select')}</Th>
                      <Th>{t('contractRegistry.metaRefund.payers.columns.address', 'Payer address')}</Th>
                      <Th>{t('contractRegistry.metaRefund.payers.columns.paid', 'Paid amount')}</Th>
                      <Th>{t('contractRegistry.metaRefund.payers.columns.refund', 'Refund amount')}</Th>
                      <Th>{t('contractRegistry.metaRefund.payers.columns.tx', 'Payment transaction')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {refundData.userPayments.map((payment, index) => {
                      const paymentKey = getPaymentKey(payment, index)
                      const state = recipients[paymentKey]
                      const explorerUrl = payment.transactionHash
                        ? getExplorerTxUrl(chainId || '0', payment.transactionHash)
                        : undefined
                      const rowAvailableRaw = availablePerPayment[index] ?? 0n
                      const hasRowAvailable = rowAvailableRaw > 0n
                      const currentRaw = state ? decimalToRaw(state.inputAmount || '0') : '0'
                      const exceedsLimit = state?.selected && BigInt(currentRaw) > BigInt(state.maxRawAmount)

                      return (
                        <Tr key={paymentKey}>
                          <Td>
                            <Button
                              size="xs"
                              colorScheme={state?.selected ? 'green' : 'gray'}
                              variant={state?.selected ? 'solid' : 'outline'}
                              onClick={() => handleToggleRecipient(paymentKey)}
                              isDisabled={!hasRowAvailable}
                            >
                              {state?.selected
                                ? t('contractRegistry.metaRefund.payers.selected', 'Selected')
                                : t('contractRegistry.metaRefund.payers.select', 'Select')}
                            </Button>
                          </Td>
                          <Td>
                            <Stack spacing={0}>
                              <Text fontSize="sm" fontFamily="mono">
                                {payment.address}
                              </Text>
                              <Text fontSize="xs" color={mutedText}>
                                {payment.createdAt ? formatRelativeTime(payment.createdAt) : ''}
                              </Text>
                            </Stack>
                          </Td>
                          <Td>
                          {hasKnownTokenDecimals
                            ? formatRefundAmount(
                                decimalToRaw(getPaymentAmountUsd(payment)),
                                chainId || '0',
                                tokenAddress,
                                tokenSymbol,
                                tokenDecimals ?? undefined
                              )
                            : t(
                                'contractRegistry.metaRefund.errors.tokenMetadataMissing',
                                'Token decimals are unavailable for this order. Meta Refund is disabled until refund metadata is complete.'
                              )}
                          </Td>
                          <Td>
                            <FormControl isInvalid={Boolean(exceedsLimit)}>
                              <Input
                                size="sm"
                                value={state?.inputAmount || ''}
                                onChange={(event) => handleAmountChange(paymentKey, event.target.value)}
                                placeholder={
                                  hasKnownTokenDecimals
                                    ? formatAmountWithDecimals(
                                        decimalToRaw(getPaymentAmountUsd(payment)),
                                        tokenDecimals as number,
                                        2
                                      )
                                    : ''
                                }
                                isDisabled={!state?.selected || !hasKnownTokenDecimals}
                              />
                            </FormControl>
                          </Td>
                          <Td>
                            {payment.transactionHash ? (
                              <HStack spacing={2}>
                                <Text fontSize="sm" fontFamily="mono" noOfLines={1} maxW="180px">
                                  {payment.transactionHash}
                                </Text>
                                <Tooltip label={t('contractRegistry.metaRefund.actions.copy', 'Copy transaction hash')}>
                                  <IconButton
                                    aria-label="copy-payment"
                                    size="xs"
                                    icon={<CopyIcon />}
                                    variant="ghost"
                                    onClick={() => navigator.clipboard.writeText(payment.transactionHash!)}
                                  />
                                </Tooltip>
                                {explorerUrl && (
                                  <Tooltip label={t('contractRegistry.metaRefund.actions.viewExplorer', 'Open in explorer')}>
                                    <IconButton
                                      aria-label="open-payment"
                                      size="xs"
                                      icon={<ExternalLinkIcon />}
                                      variant="ghost"
                                      onClick={() => window.open(explorerUrl, '_blank')}
                                    />
                                  </Tooltip>
                                )}
                              </HStack>
                            ) : (
                              <Text fontSize="sm" color={mutedText}>
                                {t('contractRegistry.metaRefund.payers.noTx', 'No transaction hash recorded')}
                              </Text>
                            )}
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              ) : showNoConfirmedPayments ? (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    {t('contractRegistry.metaRefund.payers.empty', 'No confirmed payments found. Meta Refund cannot be executed.')}
                  </AlertDescription>
                </Alert>
              ) : null}
            </Box>

            <Divider />

            <FormControl isRequired>
              <FormLabel>{t('contractRegistry.metaRefund.reason.label', 'Refund reason')}</FormLabel>
              <Textarea
                placeholder={t('contractRegistry.metaRefund.reason.placeholder', 'Enter the reason for this Meta Refund...')}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
                maxLength={REASON_MAX_LENGTH}
                isDisabled={!canExecuteRefundWorkflow || !hasKnownTokenDecimals}
              />
              <HStack justify="space-between" mt={1}>
                <FormHelperText>
                  {t('contractRegistry.metaRefund.reason.helper', 'This reason will be stored in the refund operation log for auditing purposes.')}
                </FormHelperText>
                <Text fontSize="xs" color={mutedText}>
                  {reason.length}/{REASON_MAX_LENGTH}
                </Text>
              </HStack>
            </FormControl>
              </>
            )}
              </>
            ) : null}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {canExecuteRefundWorkflow && (
            <Tooltip isDisabled={!submitTooltipLabel} label={submitTooltipLabel}>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isDisabled={!hasActiveRecipients || isSubmissionDisabled}
                isLoading={metaRefundMutation.isPending || isSubmittingEvmRefund}
              >
                {metaRefundMutation.isPending || isSubmittingEvmRefund
                  ? t('contractRegistry.metaRefund.actions.submitting', 'Submitting...')
                  : t('contractRegistry.metaRefund.actions.submit', 'Submit Meta Refund')}
              </Button>
            </Tooltip>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
